/*
 * Copyright (c) 2021. Dave van Rijn Development
 */
import './style/pdfElement.scss';

import React, {MouseEventHandler, PropsWithChildren, PureComponent} from 'react';
import {DraggableData, HandleClasses, HandleStyles, ResizeEnable, Rnd} from 'react-rnd';
import {
    CustomAppEvent, ElementPosition, IndexedObject, PDFElementDimensions, PDFElementParams, PDFElementPersist, PdfFont,
    RNDDimensions
} from "../../util/interfaces";
import {generateComponentId} from "../../util/componentUtil";
import {PAGE_WIDTH, pxToPt} from "../../util/pdfUtil";
import classNames from 'classnames';
import {AwesomeIcon, debug} from "../../../../index";

interface State {
    dragging: boolean;
    settings: IndexedObject<any>;
}

interface SharedProps {
    onFocusElement: (element: PdfElement) => void;
    onDelete: MouseEventHandler;
    onSaveHistory: VoidFunction;
    persistent?: PDFElementPersist;
}

interface Props {
    id?: string;
}

export abstract class PdfElement<P extends SharedProps = any, S extends State = any> extends PureComponent<P & Props, S> {
    protected readonly id = generateComponentId(this.props.id);
    protected element: HTMLDivElement;
    protected readonly event: CustomAppEvent;
    protected draggable: PDFDraggable;
    abstract getParams: (convertPixels?: boolean) => PDFElementParams<any, any>;
    abstract onClick: (evt: React.MouseEvent) => void;
    abstract onClickDelete: (evt: React.MouseEvent) => void;

    protected constructor(props: Props & P) {
        super(props);
        this.event = {eventName: 'pdfFontChanged', handler: this.setFont};
    }

    onDragStart = () => {
        this.setState({dragging: true});
    };

    onDragEnd = () => {
        this.setState({dragging: false});
    };

    setFont = (data: { previousFont: PdfFont, font: PdfFont }) => {
        const {previousFont, font} = data, {settings} = this.state;
        if (settings.hasOwnProperty('font')) {
            if (settings['font'] === previousFont)
                this.setState({settings: Object.assign({}, settings, {font})});
        }
    };

    getDimensions = (convertPixels: boolean): PDFElementDimensions => {
        if (this.element) {
            const parent = this.element.parentElement?.parentElement, rect = this.element.getBoundingClientRect();
            const width = convertPixels ? pxToPt(rect.width) : rect.width,
                height = convertPixels ? pxToPt(rect.height) : rect.height;
            if (parent) return {
                left: this.getLeft(parent, convertPixels),
                top: this.getTop(parent, convertPixels),
                width,
                height,
            };
        }
        return {left: 0, top: 0, width: 0, height: 0};
    };

    public getSettings = () => this.state.settings;

    public setSetting = (key: string, value: any, callback?: VoidFunction, save: boolean = true) => {
        this.setState({settings: Object.assign({}, this.state.settings, {[key]: value})}, () => {
            if (save)
                this.props.onSaveHistory();
            if (callback) callback();
        });
    };

    public changeSetting = (key: string) => (value: any) => {
        this.setSetting(key, value);
    };

    public isPersistent = (): PDFElementPersist => {
        return this.props.persistent ?? PDFElementPersist.NOT_PERSISTENT;
    }

    private getLeft = (parent: HTMLElement, convertPixels: boolean) => {
        const rect = this.element.getBoundingClientRect(), parentRect = parent.getBoundingClientRect(),
            left = Math.abs(rect.left - parentRect.left);
        if (convertPixels)
            return pxToPt(left);
        return left;
    };

    private getTop = (parent: HTMLElement, convertPixels: boolean) => {
        const rect = this.element.getBoundingClientRect(), parentRect = parent.getBoundingClientRect(),
            top = Math.abs(rect.top - parentRect.top);
        if (convertPixels)
            return pxToPt(top);
        return top;
    };
}

interface DraggableProps {
    onDrag?: (position: { x: number; y: number; }) => void;
    onDragStart: VoidFunction;
    onDragEnd: (position: { x: number; y: number; }) => void;
    onDragged: VoidFunction;
    onResized: VoidFunction;
    onDelete?: MouseEventHandler;
    onEdit?: MouseEventHandler;
    onClickBar: MouseEventHandler;
    id?: string;
    dragging: boolean;
    forwardRef?: any;
    elementLabel: string;
    dimensions?: RNDDimensions;
    draggable: boolean;
    resizeable: boolean;
    resizeHoriz: boolean;
    resizeVert: boolean;
    position?: { x: number; y: number };
    lockAspect?: boolean;
    maxWidth: number;
    maxHeight: number;
    width: number;
    height: number;
    matchParentSize: boolean;
    alignment?: ElementPosition;
    persistent?: PDFElementPersist;
}

interface DraggableState {
    position: { x: number; y: number };
    width: number | string;
    height: number | string;
}

export class PDFDraggable extends PureComponent<PropsWithChildren<DraggableProps>, DraggableState> {
    static defaultProps = {
        resizeable: true,
        resizeHoriz: true,
        resizeVert: true,
        maxWidth: Number.MAX_SAFE_INTEGER,
        maxHeight: Number.MAX_SAFE_INTEGER,
        lockAspect: false,
        width: 'auto',
        height: 'auto',
        matchParentSize: false,
    };
    private readonly id = generateComponentId(this.props.id);
    private draggable: Rnd;

    constructor(props: DraggableProps) {
        super(props);
        this.state = {
            position: this.props.position || {x: 0, y: 0},
            width: props.width || 'auto',
            height: props.height || 'auto',
        };
    }

    public setSize = (width: number, height: number, callListener: boolean = true) => {
        this.setState({width, height}, () => {
            if (callListener)
                this.props.onResized();
        });
    };

    public setFocus = () => {
        if (this.draggable) {
            const div = this.draggable.resizableElement.current;
            if (div) div.classList.add('focused');
        }
    };

    public setPosition = (position: { x?: number; y?: number }, callback?: VoidFunction) => {
        this.setState({position: Object.assign({}, this.state.position, position)}, callback);
    };

    public getPosition = (): { x: number; y: number } => this.state.position;

    onDrag = (evt: any, data: DraggableData) => {
        const {onDrag, alignment} = this.props, {position} = this.state,
            nextPosition = {x: alignment ? position.x : data.x, y: data.y};
        if (onDrag) {
            onDrag(nextPosition);
        }
    };

    onDragStop = (evt: any, data: any) => {
        const {onDragEnd, alignment, onDragged} = this.props, {position} = this.state,
            nextPosition = {x: alignment ? position.x : data.lastX, y: data.lastY};
        debug('Stop drag at', {nextPosition});
        this.setState({position: nextPosition});
        onDragEnd(nextPosition);
        onDragged();
    };

    onResizeStop = (evt: any, direction: any, ref: HTMLElement, delta: any, position: any) => {
        this.setState({
            width: ref.style.width,
            height: ref.style.height,
            position
        }, this.props.onResized);
    };

    setInitialPosition = () => {
        const {alignment} = this.props, {position} = this.state;
        if (!alignment) return;
        const div = document.getElementById(this.id);
        if (div) {
            const width = div.getBoundingClientRect().width;
            if (alignment === ElementPosition.CENTER)
                this.setState({position: {x: PAGE_WIDTH / 2 - width / 2, y: position.y}});
            else if (alignment === ElementPosition.LEFT)
                this.setState({position: {x: 75, y: position.y}});
            else if (alignment === ElementPosition.RIGHT)
                this.setState({position: {x: PAGE_WIDTH - 75 - width, y: position.y}});
        }
    }

    getHandleClasses = (): HandleClasses => {
        const cls = 'resize-handle';
        return {
            bottomLeft: `${cls} bottom left`,
            bottomRight: `${cls} bottom right`,
            topLeft: `${cls} top left`,
            topRight: `${cls} top right`,
        };
    };

    getHandleStyles = (): HandleStyles => {
        const style: React.CSSProperties = {width: '10px', height: '10px'};
        return {
            topRight: {...style, top: '-5px', right: '-5px'},
            topLeft: {...style, top: '-5px', left: '-5px'},
            bottomRight: {...style, bottom: '-5px', right: '-5px'},
            bottomLeft: {...style, bottom: '-5px', left: '-5px'},
        }
    };

    getEnabledResize = (): ResizeEnable => {
        const {resizeable, resizeHoriz, resizeVert} = this.props;
        if (!resizeable) return false;
        if (resizeHoriz && resizeVert) return true;
        return {
            bottom: resizeVert,
            bottomLeft: resizeVert && resizeHoriz,
            bottomRight: resizeHoriz && resizeVert,
            left: resizeHoriz,
            right: resizeHoriz,
            top: resizeVert,
            topLeft: resizeHoriz && resizeVert,
            topRight: resizeHoriz && resizeVert,
        };
    };

    componentDidMount = () => {
        window.setTimeout(this.setInitialPosition, 20);
    };

    componentDidUpdate = (prevProps: DraggableProps) => {
        if (prevProps.alignment !== this.props.alignment)
            window.setTimeout(this.setInitialPosition, 20);
    };

    render = () => {
        const {
                children, forwardRef, onDragStart, dragging, draggable, persistent,
                maxHeight, maxWidth, lockAspect, matchParentSize, onEdit, alignment
            } = this.props, {width, height, position} = this.state,
            axis = alignment ? 'y' : 'both',
            onDelete = persistent === PDFElementPersist.PERSISTENT ? undefined : this.props.onDelete;
        return (
            <Rnd position={position} dragAxis={axis} onDragStart={onDragStart} onDragStop={this.onDragStop}
                 bounds='parent' className={classNames('pdf-draggable', dragging && 'dragging')}
                 disableDragging={!draggable} enableResizing={this.getEnabledResize()} lockAspectRatio={lockAspect}
                 maxWidth={maxWidth} maxHeight={maxHeight} size={{width, height}} onDrag={this.onDrag}
                 resizeHandleClasses={this.getHandleClasses()} resizeHandleStyles={this.getHandleStyles()}
                 onResizeStop={this.onResizeStop} dragHandleClassName='drag-handle' dragGrid={[10, 10]}
                 ref={(ref: Rnd) => {
                     this.draggable = ref
                 }}>
                <div className='action-bar'>
                    {draggable ? <AwesomeIcon name='arrows-alt' className='action drag-handle'/> : <div/>}
                    <div className='options'>
                        {onEdit !== undefined && <AwesomeIcon name='pen' onClick={onEdit} className='action'/>}
                        {onDelete !== undefined &&
                        <AwesomeIcon name='trash-alt' onClick={onDelete} className='action delete'/>}
                    </div>
                </div>
                <div className={classNames('pdf-element', 'rnd', matchParentSize && 'match-size')} ref={forwardRef}
                     id={this.id}>
                    {dragging && <>
                        <div className='guide vert'/>
                        <div className='guide vert reverse'/>
                        <div className='guide horiz'/>
                        <div className='guide horiz reverse'/>
                    </>}
                    {children}
                </div>
            </Rnd>
        )
    }
}
